// Library
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../../i18n";

// Components
import UserAttachmentsDialog from "./UserAttachmentsDialog";
import InviteDialog from "../invite";
import DownloadBuilder from "../../components/DownloadBuilder";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppTableToolbar from "../../design-system/components/AppTableToolbar";
import AppWorkspace from "../../design-system/components/AppWorkspace";

// Icons
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { IconDelete, IconImport } from "../../design-system/icons";

// Navigation

//Others
import { API_BASE_URL } from "../../config";

import TeamMainGrid from "./team_overview";
import AvailabilityDateField from "../availability/AvailabilityDateField";
import { formatAccessLevel } from "../../utils/accessLevels";
import { isDemoMode } from "../../demoMode";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./team.css";

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const monthStartKey = (month) => `${month}-01`;

const monthEndKey = (month) => {
  const [year, monthIndex] = month.split("-").map(Number);
  return toDateKey(new Date(year, monthIndex, 0));
};

const defaultExportTimeframe = () => {
  const month = toDateKey(new Date()).slice(0, 7);
  return {
    mode: "month",
    month,
    start_date: monthStartKey(month),
    end_date: monthEndKey(month),
  };
};


const Team = () => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [tablePaneWidth, setTablePaneWidth] = useState(null);
  const tablePaneObserverRef = useRef(null);
  const [accessLevel, setAccessLevel] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem("session_token");
  const [columnVisibility, setColumnVisibility] = useState({});
  // Kürzel und Farbe je Abteilung — beides haengt an derselben Quelle und wird
  // deshalb in einer Zuordnung gehalten (Suche nutzt das Kürzel, die Tabelle
  // faerbt die Chips mit der Farbe).
  const [departmentsById, setDepartmentsById] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportFilters, setExportFilters] = useState({});
  const [exportTimeframe, setExportTimeframe] = useState(() => defaultExportTimeframe());
  const [exportTabMode, setExportTabMode] = useState("none");
  const [exportResetKey, setExportResetKey] = useState(0);
  const [isExportPreviewLoading, setIsExportPreviewLoading] = useState(false);
  const navigate = useNavigate();
  const demoMode = isDemoMode();

  const [attachmentsUser, setAttachmentsUser] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [documentsFeatureEnabled, setDocumentsFeatureEnabled] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [userIdToReset, setUserIdToReset] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");

  const setTablePaneRef = useCallback((node) => {
    tablePaneObserverRef.current?.disconnect();
    tablePaneObserverRef.current = null;

    if (!node) return;

    setTablePaneWidth(node.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      setTablePaneWidth(entry.contentRect.width);
    });
    observer.observe(node);
    tablePaneObserverRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      tablePaneObserverRef.current?.disconnect();
    },
    []
  );

  const ColumnMapping = {
    column1: "first_name",
    column2: "last_name",
    column3: "email",
    column4: "departments",
    column5: "employment",
    column6: "employment_level",
    column10: "access_level",
  };

  const fetchUsers = async ({ showLoading = false } = {}) => {
    if (showLoading) setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/team/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data.users);
      setAccessLevel(response.data.access_level);
      setDepartmentsById(Object.fromEntries(
        (response.data.department_options || []).map((department) => [
          String(department.id),
          {
            shorthand: department.shorthand_symbol || department.name,
            color: department.color_hex || "",
          },
        ])
      ));

      const updatedColumnVisibility = {};
      Object.values(ColumnMapping).forEach((frontendKey) => {
        updatedColumnVisibility[frontendKey] = true;
      });
      setColumnVisibility(updatedColumnVisibility);
      return response.data;
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers({ showLoading: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      const haystack = [
        user.first_name,
        user.last_name,
        user.email,
        ...(user.selected_departments || []),
        ...(user.selected_department_ids || []).map(
          (departmentId) => departmentsById[String(departmentId)]?.shorthand
        ),
        user.employment,
        user.employment_level != null ? `${Number(user.employment_level) * 100}%` : "",
        user.access_level_label || formatAccessLevel(user.access_level),
        user.access_level,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [departmentsById, searchTerm, users]);

  useEffect(() => {
    const fetchDocumentUploadFeature = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/web/feature-special/document_upload`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDocumentsFeatureEnabled(!!response?.data?.enabled);
      } catch (_error) {
        setDocumentsFeatureEnabled(false);
      }
    };

    fetchDocumentUploadFeature();
  }, [token]);


  const handleAttachmentsOpen = (user) => {
    setAttachmentsUser(user);
  };

  const handleAttachmentsClose = () => {
    setAttachmentsUser(null);
  };

  const handleAttachmentCountChange = useCallback((userId, attachmentCount) => {
    setUsers((currentUsers) => currentUsers.map((user) => (
      user.id === userId
        ? { ...user, attachment_count: attachmentCount }
        : user
    )));
  }, []);

  const handleDeleteDialogOpen = () => {
    if (selectedUserIds.length === 0) return;
    setDeletePassword("");
    setDeleteError("");
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    if (deleting) return;
    setOpenDeleteDialog(false);
    setDeletePassword("");
    setDeleteError("");
  };

  const selectedUserNames = useMemo(() => {
    const selected = new Set(selectedUserIds);
    return users
      .filter((user) => selected.has(user.id))
      .map((user) => [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || `#${user.id}`);
  }, [users, selectedUserIds]);

  const handleConfirmDelete = async () => {
    if (deleting || selectedUserIds.length === 0) return;
    // Nur für die Leerprüfung trimmen — der Login vergleicht das Passwort ungetrimmt.
    const password = deletePassword;
    if (!password.trim()) {
      setDeleteError(t("team.deletion.password_required"));
      return;
    }

    setDeleting(true);
    setDeleteError("");
    const userIdsToDelete = [...selectedUserIds];
    // allSettled: bei einem Fehler sollen bereits gelöschte Personen trotzdem aus der Liste verschwinden.
    const results = await Promise.allSettled(
      userIdsToDelete.map((userId) =>
        axios.delete(`${API_BASE_URL}/api/web/team/users/delete/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { password },
        })
      )
    );
    const deletedUserIds = new Set(
      userIdsToDelete.filter((_, index) => results[index].status === "fulfilled")
    );
    const failure = results.find((result) => result.status === "rejected");

    setUsers((prev) => prev.filter((user) => !deletedUserIds.has(user.id)));
    setSelectedUserIds((prev) => prev.filter((id) => !deletedUserIds.has(id)));
    setDeleting(false);

    if (failure) {
      setDeleteError(
        failure.reason?.response?.data?.message || t("team.deletion.failed")
      );
      return;
    }
    setOpenDeleteDialog(false);
    setDeletePassword("");
  };

  const handlePasswordDialogOpen = (id) => {
    setUserIdToReset(id);
    setPasswordError("");
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setOpenPasswordDialog(true);
  };

  const closePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setUserIdToReset(null);
    setPasswordError("");
    setPasswordData({ newPassword: "", confirmPassword: "" });
  };

  // Nur der Weg über Abbrechen/Backdrop ist während des Speicherns gesperrt.
  const handlePasswordDialogClose = () => {
    if (passwordSaving) return;
    closePasswordDialog();
  };

  const handlePasswordChange = (event) =>
    setPasswordData((prev) => ({ ...prev, [event.target.name]: event.target.value }));

  const handlePasswordReset = async () => {
    if (!passwordData.newPassword) {
      setPasswordError(t("change_password.require_content"));
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t("change_password.no_succes_password"));
      return;
    }

    setPasswordSaving(true);
    setPasswordError("");
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/team/users/${userIdToReset}/password`,
        { password: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closePasswordDialog();
    } catch (error) {
      // Ohne diesen Zweig blieb der Dialog bei jedem Fehler wortlos offen stehen.
      setPasswordError(
        error?.response?.data?.message
        || t("team.password.reset_failed", "Das Passwort konnte nicht gesetzt werden.")
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  const buildExportParams = (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    const params = { tab_mode: tabMode };
    if (timeframe?.mode === "range") {
      if (!timeframe.start_date || !timeframe.end_date || timeframe.end_date < timeframe.start_date) {
        return null;
      }
      params.start_date = timeframe.start_date;
      params.end_date = timeframe.end_date;
    } else {
      params.month = timeframe?.month || defaultExportTimeframe().month;
    }
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    if (filters?.access_levels?.length) {
      params.access_levels = filters.access_levels.join(",");
    }
    if (filters?.employment?.length) {
      params.employment = filters.employment.join(",");
    }
    return params;
  };

  const loadExportPreview = async (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    const params = buildExportParams(filters, timeframe, tabMode);
    if (!params) return null;
    setIsExportPreviewLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/team/users/export/preview`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setExportPreview(response.data);
      return response.data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setIsExportPreviewLoading(false);
    }
  };

  const openExportDialog = async () => {
    const filters = { access_levels: [], employment: [] };
    const timeframe = defaultExportTimeframe();
    setExportPreview(null);
    setExportFilters(filters);
    setExportTimeframe(timeframe);
    setExportTabMode("none");
    setExportOpen(true);
    const preview = await loadExportPreview(filters, timeframe, "none");
    if (!preview) return;
    setExportResetKey((current) => current + 1);
  };

  const handleExportFiltersChange = (nextFilters) => {
    setExportFilters(nextFilters);
    loadExportPreview(nextFilters, exportTimeframe).catch(() => {});
  };

  const handleExportTimeframeChange = (nextTimeframe) => {
    setExportTimeframe(nextTimeframe);
    loadExportPreview(exportFilters, nextTimeframe).catch(() => {});
  };

  const handleExportUsers = async ({
    format: exportFormat = "excel",
    columns,
    tab_mode: tabMode,
    filters,
    timeframe,
    settings,
  }) => {
    if (!buildExportParams(filters, timeframe)) {
      return;
    }
    const isPdfExport = exportFormat === "pdf";
    const extension = isPdfExport ? "pdf" : "xlsx";
    setIsExporting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/web/team/users/export`, {
        format: exportFormat,
        columns,
        tab_mode: tabMode,
        filters,
        timeframe,
        settings,
        search: searchTerm.trim(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const fileBlob = response.data instanceof Blob
        ? response.data
        : new Blob(
          [response.data],
          {
            type: isPdfExport
              ? "application/pdf"
              : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      const previewFileName = exportPreview?.file_name?.replace(/\.xlsx$/i, `.${extension}`);
      link.setAttribute("download", previewFileName || `Team.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="team-page">
      <AppWorkspace
        title={t("team.title")}
        loading={isLoading}
      >
        <AppTableToolbar
          search={{
            value: searchTerm,
            onChange: (event) => setSearchTerm(event.target.value),
            placeholder: "Mitarbeitende suchen",
          }}
          utilityActions={[
            {
              label: "Download erstellen",
              icon: <IconImport />,
              iconOnly: true,
              tooltip: "Download erstellen",
              variant: "neutral",
              size: "md",
              onClick: openExportDialog,
              disabled: demoMode || isExporting || isExportPreviewLoading,
            },
            !demoMode && ["Super_Admin", "Admin", "company_admin"].includes(accessLevel) && {
              label: t("team.deletion.delete_selected"),
              icon: <IconDelete />,
              iconOnly: true,
              tooltip: t("team.deletion.delete_selected"),
              variant: selectedUserIds.length > 0 ? "error" : "muted",
              size: "md",
              onClick: handleDeleteDialogOpen,
              disabled: selectedUserIds.length === 0,
            },
          ].filter(Boolean)}
          primaryAction={!demoMode ? {
            label: t("registration.invite") || "Invite User",
            icon: <PersonAddAltIcon />,
            variant: "primary",
            size: "md",
            onClick: () => setInviteDialogOpen(true),
          } : null}
        />
        <AppTablePane ref={setTablePaneRef}>
          <TeamMainGrid
            users={filteredUsers}
            columnVisibility={columnVisibility}
            departmentsById={departmentsById}
            accessLevel={accessLevel}
            availableWidth={tablePaneWidth}
            documentsEnabled={documentsFeatureEnabled}
            t={t}
            onEditUser={(id) => navigate(`/team/${id}/edit`)}
            onAttachmentsOpen={handleAttachmentsOpen}
            onPasswordDialogOpen={handlePasswordDialogOpen}
            selectedUserIds={selectedUserIds}
            onSelectedUserIdsChange={setSelectedUserIds}
            readOnly={demoMode}
          />
        </AppTablePane>
      </AppWorkspace>
      <DownloadBuilder
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Download erstellen"
        documentTitle="Team"
        description={`${exportPreview?.period_label || "Aktueller Zeitraum"} · ${exportPreview?.scope_label || "Sichtbare Mitarbeitende"}`}
        columns={exportPreview?.columns || []}
        defaultColumnIds={exportPreview?.default_column_ids || []}
        tabModes={exportPreview?.tab_modes || []}
        defaultTabMode={exportPreview?.default_tab_mode || "none"}
        timeframeOptions={[
          { id: "month", label: "Monat" },
          { id: "range", label: "Benutzerdefiniert" },
        ]}
        defaultTimeframe={exportTimeframe}
        onTimeframeChange={handleExportTimeframeChange}
        filterGroups={exportPreview?.filter_groups || []}
        defaultFilters={exportPreview?.default_filters || exportFilters}
        resetKey={exportResetKey}
        onFiltersChange={handleExportFiltersChange}
        onTabModeChange={(tabMode) => {
          setExportTabMode(tabMode);
          loadExportPreview(exportFilters, exportTimeframe, tabMode).catch(() => {});
        }}
        previewRows={exportPreview?.preview_rows || []}
        previewSections={exportPreview?.preview_sections || []}
        previewLoading={isExportPreviewLoading}
        isDownloading={isExporting}
        onDownload={handleExportUsers}
        dateFieldComponent={AvailabilityDateField}
        pdfEnabled
      />
      <AppDialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        size="sm"
      >
        <AppDialogHeader
          title={t("team.deletion.bulk_title")}
          subtitle={selectedUserIds.length === 1
            ? t("team.deletion.question")
            : t("team.deletion.bulk_question", { count: selectedUserIds.length })}
        />
        <AppDialogBody>
          <AppFieldStack size="md">
            {selectedUserNames.length > 0 ? (
              <ul className="team-delete-dialog__names" aria-label={t("team.deletion.bulk_title")}>
                {selectedUserNames.map((name, index) => (
                  <li key={`${name}-${index}`}>{name}</li>
                ))}
              </ul>
            ) : null}
            <AppCallout
              variant="error"
              title={t("team.deletion.permanent_title")}
              description={t("team.deletion.permanent_note")}
            />
            <AppField
              label={t("team.deletion.password_label")}
              error={deleteError || undefined}
            >
              <AppInput
                type="password"
                name="deletePassword"
                autoFocus
                invalid={Boolean(deleteError)}
                autoComplete="current-password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleConfirmDelete();
                }}
                disabled={deleting}
              />
            </AppField>
          </AppFieldStack>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" onClick={handleDeleteDialogClose} disabled={deleting}>
            {t("button.cancel")}
          </AppButton>
          <AppButton
            variant="error"
            onClick={handleConfirmDelete}
            disabled={deleting || !deletePassword.trim()}
          >
            <IconDelete />
            {t("team.deletion.confirm_permanent")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>
      <AppDialog
        open={openPasswordDialog}
        onClose={handlePasswordDialogClose}
        size="sm"
      >
        <AppDialogHeader
          title={t("team.password.reset_title")}
          subtitle={t("team.password.reset_subtitle")}
        />
        <AppDialogBody>
          <AppFieldStack size="md">
            <AppField label={t("change_password.password")} error={passwordError || undefined}>
              <AppInput
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              />
            </AppField>
            <AppField label={t("change_password.password2")}>
              <AppInput
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              />
            </AppField>
          </AppFieldStack>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" onClick={handlePasswordDialogClose} disabled={passwordSaving}>
            {t("cancel")}
          </AppButton>
          <AppButton variant="primary" onClick={handlePasswordReset} disabled={passwordSaving}>
            {t("confirm")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>
      {documentsFeatureEnabled ? (
        <UserAttachmentsDialog
          open={Boolean(attachmentsUser)}
          user={attachmentsUser}
          canUpload={["Super_Admin", "Admin", "company_admin"].includes(accessLevel)}
          onClose={handleAttachmentsClose}
          onCountChange={handleAttachmentCountChange}
        />
      ) : null}
      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInvited={() => fetchUsers()}
      />
    </div>
  );
};

export default Team;
